import * as React from "react";
import { Book } from "../../types/Book";
import FormControl from "@material-ui/core/FormControl";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import "./EditBookCard.css";
import Button from "../Button/Button";
import { Form, FormSpy } from "react-final-form";
import SelectType from "./Select/Select";
import DatesWrapper from "./DatesWrapper/DatesWrapper";
import { FormApi } from "final-form";
import {
	LibraryCard,
	CardPropertyType,
	CardType,
	CardProperties,
} from "../../types/LibraryCard";
import Notes from "./Notes/Notes";
import ExpandIcon from "@material-ui/icons/ExpandMore";
import HistoryIcon from "@material-ui/icons/History";
import Description from "@material-ui/icons/Description";
import { addDays, addMonths } from "date-fns";
import Card from "../Feed/Card/Card";
import { DatePeriod } from "../CardHistory/CardHistory";

export interface EditCardProps {
	book: Book;
	onSaved?: () => void;
	libraryCard?: LibraryCard;
	forcedSection?: CardType;
	forcedStartRead?: string;
	onlyHistoryAdd?: boolean;
	saveCard: (card: LibraryCard) => Promise<number>;
	saveProperties: (
		cardId: number,
		properties: CardProperties
	) => Promise<boolean>;
	onClose?: () => void;
	hideBookCard?: boolean;
	periodEdit?: DatePeriod;
	onHistoryEdit?: (updatedPeriod: DatePeriod) => Promise<boolean>;
}

const EditBookCard: React.FunctionComponent<EditCardProps> = (props) => {
	const [errors, setErrors] = React.useState(false);
	const [isSaving, setSaving] = React.useState(false);

	const onSubmitHandler = (data: any, form: FormApi) => {
		setSaving(true);

		const properties: CardProperties = {};

		if (props.onlyHistoryAdd || data["type"] === CardType.AlreadyRead) {
			properties[CardPropertyType.CardHistoryStart] = data["start_reading"];
			properties[CardPropertyType.CardHistoryEnd] = data["finish_reading"];

			if (!props.onlyHistoryAdd) {
				properties[CardPropertyType.Type] = data["type"];
				properties[CardPropertyType.DateBegin] = "";
				properties[CardPropertyType.DateFinish] = "";
			}
		} else {
			properties[CardPropertyType.Type] = data["type"];
			properties[CardPropertyType.DateBegin] = data["start_reading"] || "";
		}

		if (props.periodEdit) {
			props
				.onHistoryEdit({
					id: props.periodEdit.id,
					start: data["start_reading"],
					end: data["finish_reading"],
					type: CardType.AlreadyRead,
				})
				.then((result) => {
					setSaving(false);

					props.onClose();
				});
		} else if (props.libraryCard) {
			props.saveProperties(props.libraryCard.eId, properties).then((result) => {
				setSaving(false);

				if (result) {
					props.onSaved();
				} else {
					setErrors(true);
				}
			});
		} else {
			props
				.saveCard({
					cardType: data["type"],
					book: props.book,
					properties,
				})
				.then((result) => {
					setSaving(false);

					if (result) {
						props.onSaved();

						window.location.href = `/books/#/${result}`;
					} else {
						setErrors(false);
					}
				});

			if ((window as any)["ym"]) {
				(window as any).ym(63827389, "reachGoal", "book_search_addBook", {
					bookId: props.book.externalId,
					bookName: props.book.name,
				});
			}
		}
	};

	const getInitialValues = React.useMemo((): any => {
		const today = new Date().toISOString();

		if (props.onlyHistoryAdd || props.periodEdit) {
			return {
				type: CardType.AlreadyRead,
				start_reading: props.periodEdit ? props.periodEdit.start : null,
				finish_reading: props.periodEdit ? props.periodEdit.end : null,
			};
		}

		if (props.forcedSection) {
			return {
				type: props.forcedSection,
				start_reading: props.forcedStartRead,
				finish_reading: null,
			};
		}

		if (props.libraryCard) {
			return {
				type: props.libraryCard.cardType,
				start_reading: props.libraryCard.properties?.["date_begin"]
					? props.libraryCard.properties["date_begin"]
					: today,
				finish_reading: props.libraryCard.properties?.["date_finish"]
					? props.libraryCard.properties["date_finish"]
					: today,
			};
		}

		return {
			type: "read_now",
			start_reading: today,
			finish_reading: today,
		};
	}, [props.book, props.libraryCard]);

	return (
		<div className="edit__wrapper">
			{!props.hideBookCard && (
				<div>
					<Card
						toBookPage={true}
						book={{
							...props.book,
							card: props.libraryCard
								? {
										id: props.libraryCard.eId,
										type: props.libraryCard.cardType,
								  }
								: null,
						}}
					/>
				</div>
			)}

			<div className="edit__form">
				<Form
					onSubmit={onSubmitHandler}
					initialValues={getInitialValues}
					mutators={{
						setStartDate: (arg, state, tools) => {
							tools.changeValue(state, "start_reading", (value) => arg[0]);
						},
					}}
				>
					{(form) => {
						const selectedType = (form.values as any)["type"];
						const readOnly =
							props.libraryCard?.cardType === CardType.AlreadyRead &&
							!props.onlyHistoryAdd &&
							selectedType === CardType.AlreadyRead &&
							!props.periodEdit;

						const setFastDate = (
							type: "tomorrow" | "next_week" | "next_month"
						) => {
							const date =
								type !== "next_month"
									? addDays(new Date(), type === "tomorrow" ? 1 : 7)
									: addMonths(new Date(), 1);

							form.form.mutators.setStartDate(date.toISOString());
						};

						return (
							<form onSubmit={form.handleSubmit}>
								<FormControl>
									<div className="edit__section__header">
										{props.libraryCard ? "Изменить" : "Добавить в библиотеку"}
									</div>

									{!props.forcedSection && !props.periodEdit && (
										<div className="edit__section edit__section_type">
											<SelectType disabled={!!props.forcedSection} />
										</div>
									)}

									{selectedType === CardType.WantRead && (
										<div>
											<div
												className="edit__suggest-date"
												onClick={() => setFastDate("tomorrow")}
											>
												завтра
											</div>

											<div
												className="edit__suggest-date"
												onClick={() => setFastDate("next_week")}
											>
												через неделю
											</div>

											<div
												className="edit__suggest-date"
												onClick={() => setFastDate("next_month")}
											>
												через месяц
											</div>
										</div>
									)}

									{!readOnly && (
										<div className="edit__section edit__section_dates">
											<div className="edit__section__fields">
												<DatesWrapper
													withEndDate={selectedType === "already_read"}
													dateBeginNameField="start_reading"
													dateEndNameField="finish_reading"
													hideDay={selectedType === "want_read"}
												/>
											</div>

											<div className="edit__section_hint">
												{selectedType === "want_read" &&
													"Укажите дату, с которой вы планируете начать читать книгу"}

												{selectedType === "read_now" &&
													"Укажите дату, с которой вы начали читать книгу"}

												{selectedType === "already_read" &&
													"Добавьте даты, чтобы книга появилась в вашем календаре. Вы сможете в любой момент изменить этот параметр"}
											</div>
										</div>
									)}

									{readOnly && (
										<div>
											<div className="edit__section_hint">
												Эта работа уже в списке Прочитанных книг.
											</div>
										</div>
									)}

									<input type="submit" style={{ display: "none" }} />

									{errors && (
										<div className="edit__section_hint edit__section_errorHint">
											Не удалось добавить в библиотеку. Попробуйте повторить
											попытку через некоторое время
										</div>
									)}

									<div className="edit__buttons">
										{props.onClose && (
											<Button onClick={() => props.onClose()}>Отменить</Button>
										)}

										<Button
											type="primary"
											onClick={() => !readOnly && form.handleSubmit()}
											isLoading={isSaving}
										>
											Сохранить
										</Button>
									</div>
								</FormControl>
							</form>
						);
					}}
				</Form>
			</div>
		</div>
	);
};

export default EditBookCard;
